/** The views of the administration screens, as the address bar holds them. A default is not written. */
import { instantIn, MARKET_TIME_ZONE } from "@/shared/lib/format";

import type { ActionsFilter, AuditFilter, DeliveriesFilter } from "../api/queries";
import { DELIVERY_STATES, type DeliveryState } from "../api/types";

function text(value: unknown): string | undefined {
  const string = typeof value === "number" ? String(value) : value;
  return typeof string === "string" && string.trim() !== "" ? string.trim() : undefined;
}

function pageOf(value: unknown): number | undefined {
  const page = Number(value);
  return Number.isInteger(page) && page > 1 ? page : undefined;
}

/** A day as a date field writes it; anything else is not a day. */
function dayOf(value: unknown): string | undefined {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split("-").map(Number) as [number, number, number];
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? value : undefined;
}

/** The day after a day: where "the whole of `to`" ends. */
function dayAfter(day: string): string {
  const [year, month, date] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(year, month - 1, date + 1)).toISOString().slice(0, 10);
}

/** The market a settings screen shows, where there are several. */
export interface MarketSearch {
  market?: string;
}

export function parseMarketSearch(raw: Readonly<Record<string, unknown>>): MarketSearch {
  const market = text(raw["market"]);
  return market !== undefined && /^[A-Z]{2}$/.test(market) ? { market } : {};
}

/** What v1's log is narrowed to, and the row open beside it. */
export interface AuditSearch {
  /** What was done to what kind of thing: `order:status_change`. */
  kind?: string;
  /** v1's id of the thing. */
  id?: string;
  /** Done by this staff member: their user id now. */
  staff?: string;
  /** The first day, `YYYY-MM-DD`, by the market's clock. */
  from?: string;
  /** The last day, the whole of it. */
  to?: string;
  page?: number;
  /** The row open beside the list. */
  entry?: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseAuditSearch(raw: Readonly<Record<string, unknown>>): AuditSearch {
  const search: AuditSearch = {};
  const kind = text(raw["kind"]);
  if (kind !== undefined && /^[^:]+:[^:]+$/.test(kind)) search.kind = kind;
  const id = text(raw["id"]);
  if (id !== undefined) search.id = id;
  const staff = text(raw["staff"]);
  if (staff !== undefined && UUID.test(staff)) search.staff = staff;
  const from = dayOf(raw["from"]);
  if (from !== undefined) search.from = from;
  const to = dayOf(raw["to"]);
  if (to !== undefined) search.to = to;
  const page = pageOf(raw["page"]);
  if (page !== undefined) search.page = page;
  const entry = text(raw["entry"]);
  if (entry !== undefined && UUID.test(entry)) search.entry = entry;
  return search;
}

/** A kind as the filter names it, and back. */
export const kindKey = (entity: string, action: string): string => `${entity}:${action}`;

/**
 * The filter as it is asked. Days are the market's: from the start of the
 * first to the start of the day after the last (`to` is "before this moment").
 */
export function auditFilter(search: AuditSearch): AuditFilter {
  const filter: AuditFilter = {};
  if (search.kind !== undefined) {
    const [entity, action] = search.kind.split(":") as [string, string];
    filter.entity = entity;
    filter.action = action;
  }
  if (search.id !== undefined) filter.entity_id = search.id;
  if (search.staff !== undefined) filter.staff_user_id = search.staff;
  const from =
    search.from === undefined ? null : instantIn(`${search.from}T00:00`, MARKET_TIME_ZONE);
  if (from !== null) filter.from = from;
  const to =
    search.to === undefined ? null : instantIn(`${dayAfter(search.to)}T00:00`, MARKET_TIME_ZONE);
  if (to !== null) filter.to = to;
  return filter;
}

// ---- what staff do in v2 ----

/** What the trail of staff actions is narrowed to, and the row open beside it. */
export interface ActionsSearch {
  /** What was done, by the service that did it: `order:visit.assigned`. */
  kind?: string;
  /** Done by this staff member. */
  staff?: string;
  /** Done about this person: a client, a specialist, a staff member. */
  about?: string;
  /** Done to this thing: its kind and id, `visit:0198…`. */
  thing?: string;
  /** Left by this request. */
  request?: string;
  /** The first day, `YYYY-MM-DD`, by the market's clock. */
  from?: string;
  /** The last day, the whole of it. */
  to?: string;
  /** The row open beside the list. */
  entry?: string;
}

export function parseActionsSearch(raw: Readonly<Record<string, unknown>>): ActionsSearch {
  const search: ActionsSearch = {};
  const kind = text(raw["kind"]);
  if (kind !== undefined && /^[a-z_]+:[a-z_]+\.[a-z_]+$/.test(kind)) search.kind = kind;
  const staff = text(raw["staff"]);
  if (staff !== undefined && UUID.test(staff)) search.staff = staff;
  const about = text(raw["about"]);
  if (about !== undefined && UUID.test(about)) search.about = about;
  const thing = text(raw["thing"]);
  if (thing !== undefined && /^[a-z_]+:.+$/.test(thing)) search.thing = thing;
  const request = text(raw["request"]);
  if (request !== undefined) search.request = request;
  const from = dayOf(raw["from"]);
  if (from !== undefined) search.from = from;
  const to = dayOf(raw["to"]);
  if (to !== undefined) search.to = to;
  const entry = text(raw["entry"]);
  if (entry !== undefined && UUID.test(entry)) search.entry = entry;
  return search;
}

/** A kind of action as the filter names it: the service, then its action. */
export const actionKey = (service: string, action: string): string => `${service}:${action}`;

/** A thing as the address names it: its kind, then its id. */
export const thingKey = (type: string, id: string): string => `${type}:${id}`;

/**
 * The filter as it is asked. Days are the market's: from the start of the
 * first to the start of the day after the last (`to` is "before this moment").
 */
export function actionsFilter(search: ActionsSearch): ActionsFilter {
  const filter: ActionsFilter = {};
  if (search.kind !== undefined) {
    const at = search.kind.indexOf(":");
    filter.service = search.kind.slice(0, at);
    filter.action = search.kind.slice(at + 1);
  }
  if (search.staff !== undefined) filter.actor_user_id = search.staff;
  if (search.about !== undefined) filter.subject_user_id = search.about;
  if (search.thing !== undefined) {
    const at = search.thing.indexOf(":");
    filter.entity_type = search.thing.slice(0, at);
    filter.entity_id = search.thing.slice(at + 1);
  }
  if (search.request !== undefined) filter.request_id = search.request;
  const from =
    search.from === undefined ? null : instantIn(`${search.from}T00:00`, MARKET_TIME_ZONE);
  if (from !== null) filter.from = from;
  const to =
    search.to === undefined ? null : instantIn(`${dayAfter(search.to)}T00:00`, MARKET_TIME_ZONE);
  if (to !== null) filter.to = to;
  return filter;
}

// ---- what the partners were told ----

export interface DeliveriesSearch {
  /** One partner's only, by its code. */
  partner?: string;
  state?: DeliveryState;
  /** What the partners were told of one order. */
  order?: string;
  /** The delivery open beside the list. */
  delivery?: string;
}

export function parseDeliveriesSearch(raw: Readonly<Record<string, unknown>>): DeliveriesSearch {
  const search: DeliveriesSearch = {};
  const partner = text(raw["partner"]);
  // A partner's code, as hg-auth spells them: small letters, digits, `_`.
  if (partner !== undefined && /^[a-z][a-z0-9_]{0,39}$/.test(partner)) search.partner = partner;
  const state = DELIVERY_STATES.find((known) => known === raw["state"]);
  if (state !== undefined) search.state = state;
  const order = text(raw["order"]);
  if (order !== undefined && UUID.test(order)) search.order = order;
  const delivery = text(raw["delivery"]);
  if (delivery !== undefined && UUID.test(delivery)) search.delivery = delivery;
  return search;
}

/** What `GET /v2/admin/partner-deliveries` is asked: the open delivery is the screen's own. */
export function deliveriesFilter(search: DeliveriesSearch): DeliveriesFilter {
  const filter: DeliveriesFilter = {};
  if (search.partner !== undefined) filter.partner = search.partner;
  if (search.state !== undefined) filter.state = search.state;
  if (search.order !== undefined) filter.order_id = search.order;
  return filter;
}
